import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { mapApiCatalogueItemToView } from "@/lib/liveCatalogueItemMap";
import { trpc } from "@/lib/trpc";
import { notifyProductImagePreparation } from "@/lib/productImagePreparationFeedback";
import { cn } from "@/lib/utils";
import { ImagePlus, Loader2, RefreshCw, Sparkles } from "lucide-react";
import type { MockCatalogueItem } from "@/data/mockCatalogueItems";
import { toast } from "sonner";
import {
  buildCatalogueDisplayName,
  customerFriendlyNameRepetitionIssues,
} from "@shared/catalogueProductIdentity";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddProduct: (item: MockCatalogueItem) => void;
};

type IdentitySuggestion = {
  canonicalBrand: string;
  canonicalModel: string;
  productName: string;
  customerFriendlyName?: string;
  productType: string | null;
  proposedDisplayName: string;
  confidence: "high" | "medium" | "low";
  status: "exact_match" | "identified" | "needs_confirmation" | "blocked_duplicate";
  evidence: Array<{
    title: string;
    url: string;
    sourceType: "manufacturer" | "distributor" | "catalogue" | "other";
    snippet: string;
  }>;
  existingMatches: Array<{
    id: number;
    brand: string;
    model: string;
    name: string;
    displayName: string | null;
    isActive: boolean;
    matchKind: string;
    blockMessage?: string;
    canUseExisting?: boolean;
  }>;
  warnings: string[];
  blockMessage?: string | null;
  requiresAdminDuplicateReview?: boolean;
  brandNorm: string;
  modelNorm: string;
  matchKey: string;
  identityKey?: string;
  originalBrand: string;
  originalModel: string;
};

export default function AddProductFromCatalogueModal({ open, onOpenChange, onAddProduct }: Props) {
  const utils = trpc.useUtils();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [name, setName] = useState("");
  const [cleoSuggestedName, setCleoSuggestedName] = useState("");
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  const [ackNameRepetition, setAckNameRepetition] = useState(false);
  const [catalogueName, setCatalogueName] = useState("");
  const [catalogueNameManuallyEdited, setCatalogueNameManuallyEdited] = useState(false);
  const [productType, setProductType] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("Each");
  const [pricingMethod, setPricingMethod] = useState<"fixed" | "percentage_of_equipment">("fixed");
  const [defaultPercentage, setDefaultPercentage] = useState("10");
  const [description, setDescription] = useState("");
  const [imageSearchText, setImageSearchText] = useState("");
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [finalImageUrl, setFinalImageUrl] = useState<string | null>(null);
  const [showManualUrl, setShowManualUrl] = useState(false);
  const [manualImageUrl, setManualImageUrl] = useState("");
  const [identity, setIdentity] = useState<IdentitySuggestion | null>(null);
  const [staffAcknowledged, setStaffAcknowledged] = useState(false);

  const previewAi = trpc.catalogue.previewCustomAiDescription.useMutation();
  const identify = trpc.catalogue.identifyCustomProductIdentity.useMutation();
  const createCustom = trpc.catalogue.createCustomProduct.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.catalogue.listForSales.invalidate(),
        utils.catalogue.getSalesCatalogueLanding.invalidate(),
        utils.catalogue.listSalesCatalogueByBrand.invalidate(),
        utils.catalogue.searchSalesCatalogue.invalidate(),
      ]);
    },
  });
  const searchImages = trpc.catalogue.searchProductImages.useQuery(
    { query: imageSearchText, limit: 20 },
    { enabled: open && step === 3 && imageSearchText.trim().length >= 2, retry: false }
  );
  const removeBg = trpc.catalogue.removeImageBackground.useMutation();

  const isClarity = brand.trim().toLowerCase() === "clarity";
  const proposedCatalogueName = useMemo(
    () =>
      buildCatalogueDisplayName({
        brand: brand.trim(),
        model: model.trim(),
        productName: name.trim(),
      }),
    [brand, model, name],
  );

  const nameRepetitionIssues = useMemo(
    () =>
      customerFriendlyNameRepetitionIssues({
        brand,
        model,
        customerFriendlyName: name,
      }),
    [brand, model, name],
  );

  const needsAck =
    !identity ||
    identity.status === "needs_confirmation" ||
    identity.confidence === "low" ||
    (identity.warnings.length > 0 && identity.status !== "identified");

  const blockedDuplicate =
    identity?.status === "exact_match" ||
    identity?.status === "blocked_duplicate" ||
    (identity?.existingMatches.length ?? 0) > 0;

  const canContinueStep1 =
    brand.trim() &&
    model.trim() &&
    name.trim() &&
    (catalogueNameManuallyEdited ? catalogueName.trim() : proposedCatalogueName.trim()) &&
    identity &&
    !blockedDuplicate &&
    (!needsAck || staffAcknowledged) &&
    (nameRepetitionIssues.length === 0 || ackNameRepetition) &&
    (pricingMethod === "percentage_of_equipment"
      ? Number.isFinite(Number(defaultPercentage)) && Number(defaultPercentage) >= 0
      : Number.isFinite(Number(price)) && Number(price) >= 0) &&
    unit.trim();
  const canCreate = Boolean(canContinueStep1 && description.trim());
  const seedSearch = useMemo(() => [brand.trim(), model.trim()].filter(Boolean).join(" ").trim(), [brand, model]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setBrand("");
      setModel("");
      setName("");
      setCleoSuggestedName("");
      setNameManuallyEdited(false);
      setAckNameRepetition(false);
      setCatalogueName("");
      setCatalogueNameManuallyEdited(false);
      setProductType("");
      setPrice("");
      setUnit("Each");
      setPricingMethod("fixed");
      setDefaultPercentage("10");
      setDescription("");
      setImageSearchText("");
      setSelectedUrl(null);
      setFinalImageUrl(null);
      setShowManualUrl(false);
      setManualImageUrl("");
      setIdentity(null);
      setStaffAcknowledged(false);
    }
  }, [open]);

  useEffect(() => {
    if (step === 3 && !imageSearchText && seedSearch) {
      setImageSearchText(seedSearch);
    }
  }, [step, imageSearchText, seedSearch]);

  useEffect(() => {
    if (!catalogueNameManuallyEdited) {
      setCatalogueName(proposedCatalogueName);
    }
  }, [proposedCatalogueName, catalogueNameManuallyEdited]);

  const runIdentify = async () => {
    if (!brand.trim() || !model.trim()) {
      toast.error("Enter Brand and Model Number first.");
      return;
    }
    setStaffAcknowledged(false);
    setAckNameRepetition(false);
    try {
      const result = (await identify.mutateAsync({
        brand: brand.trim(),
        model: model.trim(),
      })) as IdentitySuggestion;
      setIdentity(result);
      setBrand(result.canonicalBrand || brand.trim());
      setModel(result.canonicalModel || model.trim());
      const suggested =
        result.customerFriendlyName?.trim() || result.productName?.trim() || "";
      setCleoSuggestedName(suggested);
      setNameManuallyEdited(false);
      setName(suggested);
      if (result.productType) {
        setProductType(result.productType);
      }
      if (!catalogueNameManuallyEdited) {
        setCatalogueName(result.proposedDisplayName || proposedCatalogueName);
      }
      if (result.status === "blocked_duplicate" || result.status === "exact_match") {
        toast.message(result.blockMessage || result.warnings[0] || "This product already exists in the catalogue.");
      } else if (result.status === "needs_confirmation") {
        toast.message("Cleo needs staff confirmation before this product can be created.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not identify product.");
    }
  };

  const generateAiDescription = async () => {
    if (!brand.trim() || !model.trim() || !name.trim()) {
      toast.error("Enter Brand, Model Number, and Customer Friendly Name first.");
      return;
    }
    const result = await previewAi.mutateAsync({
      brand: brand.trim(),
      model: model.trim(),
      name: name.trim(),
      unit: unit.trim() || "Each",
      category: productType.trim() || "Custom",
    });
    if (result.ok) {
      setDescription(result.text);
    } else {
      toast.error(result.error || "Could not generate description.");
    }
  };

  const createAndAdd = async () => {
    if (!canCreate || !identity || blockedDuplicate) return;
    const out = await createCustom.mutateAsync({
      brand: brand.trim(),
      model: model.trim(),
      name: name.trim(),
      displayName: (catalogueNameManuallyEdited ? catalogueName : proposedCatalogueName).trim(),
      displayNameManuallyEdited: catalogueNameManuallyEdited,
      nameManuallyEdited,
      cleoSuggestedCustomerFriendlyName: cleoSuggestedName || null,
      acknowledgeCustomerFriendlyNameRepetition: nameRepetitionIssues.length > 0 ? ackNameRepetition : undefined,
      productType: productType.trim() || null,
      price: pricingMethod === "percentage_of_equipment" ? 0 : Number(price),
      unit: unit.trim() || "Each",
      description: description.trim(),
      imageUrl: finalImageUrl,
      pricingMethod: isClarity ? pricingMethod : "fixed",
      defaultPercentage:
        isClarity && pricingMethod === "percentage_of_equipment" ? Number(defaultPercentage) : null,
      staffAcknowledgedUnverifiedIdentity: needsAck ? staffAcknowledged : undefined,
      identity: {
        source:
          identity.evidence.some((e) => e.sourceType === "manufacturer")
            ? "cleo_web_manufacturer"
            : identity.evidence.some((e) => e.sourceType === "catalogue")
              ? "cleo_catalogue"
              : "cleo_web",
        confidence: identity.confidence,
        status: identity.status,
        evidence: identity.evidence,
        originalBrand: identity.originalBrand,
        originalModel: identity.originalModel,
      },
    });
    if (!out.item) {
      toast.error("Custom product was created but could not be loaded.");
      return;
    }
    const mapped = mapApiCatalogueItemToView(out.item as any);
    onAddProduct(mapped);
    toast.success("Custom product created and added to proposal.");
    onOpenChange(false);
  };

  const useExisting = (matchId: number) => {
    void utils.catalogue.getItemForCatalogueDeepLink
      .fetch({ catalogueItemId: matchId })
      .then((item) => {
        if (!item) {
          toast.error("Existing product could not be loaded.");
          return;
        }
        const mapped = mapApiCatalogueItemToView(item as any);
        onAddProduct(mapped);
        toast.success("Existing catalogue product added to proposal.");
        onOpenChange(false);
      })
      .catch(() => toast.error("Existing product could not be loaded."));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,640px)] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Product</DialogTitle>
          <DialogDescription>
            Identify the product with Cleo, review the canonical identity, then add it to your proposal.
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs text-muted-foreground">Step {step} of 4</div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Brand</p>
                <Input
                  value={brand}
                  onChange={(e) => {
                    setBrand(e.target.value);
                    setIdentity(null);
                    setStaffAcknowledged(false);
                  }}
                  placeholder="Brand"
                />
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Model Number
                </p>
                <Input
                  value={model}
                  onChange={(e) => {
                    setModel(e.target.value);
                    setIdentity(null);
                    setStaffAcknowledged(false);
                  }}
                  placeholder="Model Number"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Enter the manufacturer’s exact product code.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => void runIdentify()}
                  disabled={!brand.trim() || !model.trim() || identify.isPending}
                >
                  {identify.isPending ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Identify with Cleo
                </Button>
              </div>

              {identity ? (
                <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3 text-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Cleo identity review
                  </p>
                  <p>
                    <strong>Brand:</strong> {identity.canonicalBrand}
                  </p>
                  <p>
                    <strong>Model Number:</strong> {identity.canonicalModel}
                  </p>
                  <p>
                    <strong>Customer Friendly Name:</strong>{" "}
                    {identity.customerFriendlyName || identity.productName || "—"}
                  </p>
                  <p>
                    <strong>Product type/category:</strong> {identity.productType || "—"}
                  </p>
                  <p>
                    <strong>Catalogue Name:</strong> {identity.proposedDisplayName}
                  </p>
                  <p>
                    <strong>Confidence:</strong> {identity.confidence}
                  </p>
                  {identity.blockMessage || identity.warnings.length > 0 ? (
                    <ul className="list-disc space-y-1 pl-4 text-amber-800 dark:text-amber-200">
                      {identity.blockMessage ? <li key="block">{identity.blockMessage}</li> : null}
                      {identity.warnings
                        .filter((w) => w !== identity.blockMessage)
                        .map((w) => (
                          <li key={w}>{w}</li>
                        ))}
                    </ul>
                  ) : null}
                  {identity.existingMatches.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Existing catalogue matches
                      </p>
                      {identity.existingMatches.map((m) => (
                        <div key={m.id} className="flex flex-wrap items-center justify-between gap-2 rounded border bg-background p-2">
                          <div>
                            <p>
                              #{m.id} {m.brand} {m.model}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {m.displayName || m.name} · {m.matchKind}
                              {m.isActive ? "" : " · inactive"}
                            </p>
                            {m.blockMessage ? (
                              <p className="text-xs text-amber-800 dark:text-amber-200">{m.blockMessage}</p>
                            ) : null}
                          </div>
                          {m.canUseExisting !== false &&
                          (m.matchKind === "exact_active" || m.matchKind === "linked_duplicate") &&
                          m.isActive ? (
                            <Button type="button" size="sm" variant="secondary" onClick={() => useExisting(m.id)}>
                              Use Existing Product
                            </Button>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {identity.evidence.length > 0 ? (
                    <div className="space-y-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Evidence / sources
                      </p>
                      {identity.evidence.slice(0, 6).map((e) => (
                        <div key={`${e.url}-${e.title}`} className="text-xs">
                          <span className="uppercase tracking-wide text-muted-foreground">[{e.sourceType}] </span>
                          {e.url.startsWith("http") ? (
                            <a href={e.url} target="_blank" rel="noreferrer" className="underline">
                              {e.title}
                            </a>
                          ) : (
                            <span>{e.title}</span>
                          )}
                          {e.snippet ? <p className="text-muted-foreground">{e.snippet}</p> : null}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Customer Friendly Name
                  </p>
                  {cleoSuggestedName ? (
                    <span className="text-[11px] text-muted-foreground">
                      {nameManuallyEdited ? "Edited by staff" : "Cleo suggested"}
                    </span>
                  ) : null}
                </div>
                <Input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameManuallyEdited(e.target.value.trim() !== cleoSuggestedName.trim());
                    setAckNameRepetition(false);
                  }}
                  placeholder="Customer Friendly Name"
                  disabled={blockedDuplicate}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  A clear name that helps customers understand what the product is.
                </p>
                {nameManuallyEdited && cleoSuggestedName ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-auto px-0"
                    onClick={() => {
                      setName(cleoSuggestedName);
                      setNameManuallyEdited(false);
                      setAckNameRepetition(false);
                    }}
                  >
                    Restore Cleo suggestion
                  </Button>
                ) : null}
                {nameRepetitionIssues.length > 0 ? (
                  <div className="mt-2 space-y-2 rounded-md border border-amber-300/60 bg-amber-50/60 p-3 text-sm dark:bg-amber-950/20">
                    {nameRepetitionIssues.map((issue) => (
                      <p key={issue}>{issue}</p>
                    ))}
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={ackNameRepetition}
                        onChange={(e) => setAckNameRepetition(e.target.checked)}
                      />
                      <span>I intentionally want to keep this wording.</span>
                    </label>
                  </div>
                ) : null}
              </div>
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Catalogue Name
                </p>
                <Input
                  value={catalogueName}
                  onChange={(e) => {
                    setCatalogueName(e.target.value);
                    setCatalogueNameManuallyEdited(true);
                  }}
                  placeholder="Catalogue Name"
                  disabled={blockedDuplicate}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  How the product will appear on proposals.
                </p>
              </div>

              {needsAck && identity && !blockedDuplicate ? (
                <label className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50/60 p-3 text-sm dark:bg-amber-950/20">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={staffAcknowledged}
                    onChange={(e) => setStaffAcknowledged(e.target.checked)}
                  />
                  <span>
                    I acknowledge that Cleo could not fully verify this identity. I am entering/confirming the
                    Customer Friendly Name manually and accept admin review of this custom product.
                  </span>
                </label>
              ) : null}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Price (SGD)</p>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    disabled={isClarity && pricingMethod === "percentage_of_equipment"}
                  />
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">UOM</p>
                  <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Each" />
                </div>
              </div>
              {isClarity ? (
                <div className="space-y-2 rounded-md border border-border p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Pricing Method</p>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="pricingMethod"
                      checked={pricingMethod === "fixed"}
                      onChange={() => setPricingMethod("fixed")}
                    />
                    Fixed Price
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="pricingMethod"
                      checked={pricingMethod === "percentage_of_equipment"}
                      onChange={() => setPricingMethod("percentage_of_equipment")}
                    />
                    Percentage of Equipment Cost
                  </label>
                  {pricingMethod === "percentage_of_equipment" ? (
                    <div>
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Default Percentage (%)
                      </p>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={defaultPercentage}
                        onChange={(e) => setDefaultPercentage(e.target.value)}
                        placeholder="10"
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex justify-between gap-2">
                <Button type="button" variant="outline" onClick={() => void generateAiDescription()} disabled={previewAi.isPending}>
                  {previewAi.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
                  {description.trim() ? "Regenerate" : "Generate AI description"}
                </Button>
              </div>
              <Textarea
                className="min-h-[260px] resize-y"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description appears here. You can edit before creating."
              />
            </div>
          )}
          {step === 3 && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input value={imageSearchText} onChange={(e) => setImageSearchText(e.target.value)} placeholder="Search image query" />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSelectedUrl(null);
                    void searchImages.refetch();
                  }}
                >
                  Find Image
                </Button>
              </div>
              {searchImages.isFetching ? (
                <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Finding product images…
                </div>
              ) : (searchImages.data?.images?.length ?? 0) > 0 ? (
                <div className="grid grid-cols-4 gap-2 md:grid-cols-5">
                  {(searchImages.data?.images ?? []).slice(0, 20).map((img) => (
                    <button
                      key={img.original}
                      type="button"
                      onClick={() => {
                        setSelectedUrl(img.original);
                        setFinalImageUrl(img.original);
                      }}
                      className={cn(
                        "overflow-hidden rounded border bg-muted/20",
                        selectedUrl === img.original ? "border-primary ring-2 ring-primary/50" : "border-border"
                      )}
                    >
                      <img src={img.thumbnail} alt={img.title ?? "Search result thumbnail"} className="h-24 w-full object-cover" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded border bg-muted/20 p-3 text-sm text-muted-foreground">
                  {searchImages.data?.configured === false
                    ? "Image search is not configured. Paste an image URL manually."
                    : searchImages.data?.message || "No images found yet."}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={!selectedUrl || removeBg.isPending}
                  onClick={async () => {
                    if (!selectedUrl) return;
                    const out = await removeBg.mutateAsync({ imageUrl: selectedUrl });
                    setFinalImageUrl(out.imageUrl || selectedUrl);
                    notifyProductImagePreparation(out);
                  }}
                >
                  {removeBg.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="mr-1.5 h-3.5 w-3.5" />}
                  Remove Background (Optional)
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowManualUrl((v) => !v)}>
                  {showManualUrl ? "Hide manual URL" : "Paste image URL"}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setFinalImageUrl(null)}>
                  Skip Image
                </Button>
              </div>
              {showManualUrl && (
                <div className="flex gap-2">
                  <Input placeholder="https://..." value={manualImageUrl} onChange={(e) => setManualImageUrl(e.target.value)} />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (!manualImageUrl.trim()) return;
                      setSelectedUrl(manualImageUrl.trim());
                      setFinalImageUrl(manualImageUrl.trim());
                    }}
                  >
                    Use URL
                  </Button>
                </div>
              )}
            </div>
          )}
          {step === 4 && (
            <div className="space-y-3 text-sm">
              <div className="rounded border bg-muted/20 p-3">
                <p><strong>Brand:</strong> {brand}</p>
                <p><strong>Model Number:</strong> {model}</p>
                <p><strong>Customer Friendly Name:</strong> {name}</p>
                <p><strong>Catalogue Name:</strong> {catalogueNameManuallyEdited ? catalogueName : proposedCatalogueName}</p>
                <p><strong>Price:</strong> SGD {Number(price || 0).toFixed(2)}</p>
                <p><strong>UOM:</strong> {unit}</p>
                <p className="mt-2 whitespace-pre-wrap"><strong>Description:</strong>{"\n"}{description || "—"}</p>
                {finalImageUrl ? (
                  <div className="mt-3">
                    <p className="mb-1"><strong>Image preview</strong></p>
                    <img src={finalImageUrl} alt="Selected product" className="max-h-44 rounded border object-contain" />
                  </div>
                ) : (
                  <p className="mt-2 text-muted-foreground"><strong>Image:</strong> Not selected</p>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="border-t pt-3 sm:justify-between">
          <Button type="button" variant="outline" onClick={() => (step === 1 ? onOpenChange(false) : setStep((s) => (Math.max(1, s - 1) as 1 | 2 | 3 | 4)))}>
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step < 4 ? (
            <Button
              type="button"
              onClick={() => setStep((s) => (Math.min(4, s + 1) as 1 | 2 | 3 | 4))}
              disabled={(step === 1 && !canContinueStep1) || (step === 2 && !description.trim())}
            >
              Next
            </Button>
          ) : (
            <Button type="button" onClick={() => void createAndAdd()} disabled={!canCreate || createCustom.isPending || blockedDuplicate}>
              {createCustom.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Create Product & Add to Proposal
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
